import { Navigate, useParams } from 'react-router-dom';
import { Clock, EyeOff, MessageCircle } from 'lucide-react';
import { AdminPage, ErrorState, Panel, Skeleton, StatusBadge } from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { Conversation } from '../Conversation';
import { useChannel } from '../api';

/**
 * כתיבה בערוץ מתוך הקונסולה (מסמך התכנון 9.5).
 *
 * קודם הייתה כאן רק הגדרת ערוצים, והכתיבה הייתה באפליקציה של
 * העובד — כלומר מנהל שיושב מול מסך רחב נשלח לעמודה ברוחב טלפון
 * כדי לכתוב משפט.
 *
 * אותו רכיב שיחה בדיוק, בתוך פאנל ברוחב הקונסולה: תיבה אחת
 * לכתיבה בשני הצדדים, ולא שתיים שאחת מהן מתיישנת.
 */
export function ChannelAdminPage() {
  const { channelId = '' } = useParams();
  const canManage = usePerm('messages.channels');
  const channel = useChannel(channelId);

  if (channel.isPending) return <Skeleton className="m-4 h-64" />;
  if (channel.isError) return <ErrorState onRetry={() => void channel.refetch()} />;
  // הפונקציה בשרת מחזירה null למי שאינו חבר בערוץ
  if (!channel.data) return <Navigate to="/admin" replace />;

  const isDirect = channel.data.kind === 'direct';
  // שיחה אישית אינה ערוץ מנוהל, ולכן נפתחת למי שבה גם בלי
  // ההרשאה לנהל ערוצים.
  if (!canManage && !isDirect) return <Navigate to="/admin" replace />;

  return (
    <AdminPage
      title={channel.data.name}
      subtitle={channel.data.description ?? undefined}
      backTo={isDirect ? '/admin/employees' : '/admin/channels'}
      width="narrow"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {isDirect && (
            <StatusBadge tone="good" icon={MessageCircle}>
              שיחה אישית
            </StatusBadge>
          )}
          {channel.data.on_shift_only && (
            <StatusBadge tone="warn" icon={Clock}>
              רק מי שבמשמרת
            </StatusBadge>
          )}
          {channel.data.is_announcement_only && (
            <StatusBadge tone="good" icon={EyeOff}>
              ערוץ רשמי
            </StatusBadge>
          )}
        </div>
      }
    >
      {/*
        p-0 כי לשיחה יש ריפוד משלה, וגובה קצוב כדי שתיבת הכתיבה
        תישאר על המסך בלי לגלול דרך כל ההיסטוריה.
      */}
      <Panel bodyClassName="p-0">
        <Conversation
          channelId={channelId}
          listClassName="max-h-[60vh] min-h-[16rem] overflow-y-auto"
        />
      </Panel>
    </AdminPage>
  );
}
